import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { projectRedux } from '../projects/project/ui/state/state';
import { sectorRedux } from '../projects/sector/ui/state/state';
import { floorRedux } from '../projects/floor/ui/state/state';
import { apartmentRedux } from '../projects/apartment/ui/state/state';
import { IProjectSector } from '../projects/project/data/entities';
import { ISector, ISectorFloor } from '../projects/sector/data/entities';
import { EApartmentStatus, IFloor, IFloorApartment } from '../projects/floor/data/entities';
import { IApartment } from '../projects/apartment/data/entities';
import { IAppReduxState } from '../../redux/store';
import { IAsyncData } from '../../core/models';
import { isError, isLoading } from '../../core/redux';
import { IImage } from '../../core/image';
import { buildPath } from '../../components/editor/path/utils';
import { ShapeViewer } from '../../components/editor/shape_viewer/shape_viewer';
import { ProjectPlan, SaleSummaryBuilding } from './types';
import { fetchApartmentAddressOptions, fetchApartmentSaleSummary } from './api';
import { Compass } from '../../components/compass';
import backdropImg from '../../../assets/images/sale-announcement/apartment-backdrop-4f466c.png';

interface IProps {
  projectPlanId: number;
  announcementId?: number;
  sectors?: IProjectSector[];
  image?: IImage;
  activePlan?: ProjectPlan;
  /** Fired when the user finishes the map cascade (picked an apartment
   *  and pressed "Növbəti"). The page-level container lifts this into
   *  its `selectedApartmentId` state and renders the shared
   *  `ApartmentDetailInline` view instead of the map. */
  onApartmentClick?: (apartment: IFloorApartment) => void;
}

/* ---------- helpers ----------------------------------------------------- */

function centroidOf(points: { x: number; y: number }[]) {
  if (!points || points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/* ---------- floor badges ------------------------------------------------ */

/** Renders the per-floor band + badge overlay on top of the building
 *  photo (done15). The Figma "Building" component composes each floor
 *  as a translucent dashed-frame BAND spanning the full floor width
 *  (Frame 566×30 px), with a small numbered BADGE centred inside it.
 *  We mirror that here:
 *
 *    • Band = DOM rectangle, positioned and sized from the floor
 *      shape's bounding box (% of the underlying image, which is
 *      drawn with `preserveAspectRatio="none"` so % maps linearly).
 *    • Badge = fixed-size pill, auto-centred in the band via flex.
 *
 *  Both react to hover and selection. The selected band gets the more
 *  opaque "active" fill + brighter dashed border; the selected badge
 *  inverts (blue fill, white border + numeral). */
interface FloorBadgesProps {
  floors: ISectorFloor[];
  image: IImage;
  selectedId?: string | null;
  hoveredId?: string | null;
  /** Subset of floor numbers that are part of the current announcement
   *  — badges/bands outside this set render greyed out and ignore
   *  clicks/hover. When undefined, every floor is enabled (back-compat). */
  enabledFloorNumbers?: Set<number>;
  onSelect: (floor: ISectorFloor) => void;
  onHover?: (floor: ISectorFloor | undefined) => void;
}

const FloorBadges: React.FC<FloorBadgesProps> = ({
  floors,
  image,
  selectedId,
  hoveredId,
  enabledFloorNumbers,
  onSelect,
  onHover,
}) => {
  if (!floors || floors.length === 0 || !image) return null;

  const imgW = image.width || 1;
  const imgH = image.height || 1;

  return (
    <div className="msp__floor-badges">
      {floors.map((floor) => {
        const pts = floor.shape?.points;
        if (!pts || pts.length === 0) return null;

        /* Bounding box of the floor shape → band's left/top/width/height. */
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        /* Shrink the band by 2% on both axes to leave a small visual
           gap between adjacent floor strips, and re-centre by
           pushing left/top half of that amount inward. */
        const rawLeftPct = (minX / imgW) * 100;
        const rawTopPct = (minY / imgH) * 100;
        const rawWidthPct = ((maxX - minX) / imgW) * 100;
        const rawHeightPct = ((maxY - minY) / imgH) * 100;
        const widthPct = Math.max(0, rawWidthPct - 2);
        const heightPct = Math.max(0, rawHeightPct - 2);
        const leftPct = rawLeftPct + (rawWidthPct - widthPct) / 2;
        const topPct = rawTopPct + (rawHeightPct - heightPct) / 2;

        const isSelected = selectedId === floor.id;
        const isHovered = hoveredId === floor.id && !isSelected;
        const enabled =
          !enabledFloorNumbers || enabledFloorNumbers.has(Number(floor.number));

        return (
          <div
            key={floor.id}
            className={
              'msp__floor-band' +
              (isSelected ? ' msp__floor-band--active' : '') +
              (isHovered ? ' msp__floor-band--hover' : '') +
              (enabled ? '' : ' msp__floor-band--disabled')
            }
            style={{
              left: `${leftPct}%`,
              top: `${topPct}%`,
              width: `${widthPct}%`,
              height: `${heightPct}%`,
            }}
            onClick={enabled ? () => onSelect(floor) : undefined}
            onMouseEnter={enabled ? () => onHover?.(floor) : undefined}
            onMouseLeave={enabled ? () => onHover?.(undefined) : undefined}
          >
            {/* Inner fill — translucent purple-blue with a backdrop
                blur. Sits behind the badge, contained by the band's
                dashed border so the frame stays crisp while the
                building photo behind the fill goes frosted. */}
            <div className="msp__floor-band-fill" aria-hidden />
            <button
              type="button"
              disabled={!enabled}
              className={
                'msp__floor-badge' +
                (isSelected ? ' msp__floor-badge--active' : '')
              }
              onClick={(e) => {
                e.stopPropagation();
                if (enabled) onSelect(floor);
              }}
            >
              {floor.number}
            </button>
          </div>
        );
      })}
    </div>
  );
};

type Building = {
  n: number;
  xPct: number;
  yPct: number;
  /* Horizontal extent of the footprint, as % of the map image. The detail
     card anchors to these rather than the centroid, so it clears the
     building instead of sitting on top of it. */
  leftPct?: number;
  rightPct?: number;
  sector?: IProjectSector;
  pathD?: string;
};

/* Per-building on-sale breakdown, keyed by building_number. Values
   come from `/apartmentSaleSummary` (announcement-scoped → only
   currently-on-sale units), already grouped server-side. */
type BuildingCounts = {
  total: number;
  byRoom: Record<string, number>; /* roomCount string ('0'..'4') → count */
};
type CountsByBuilding = Record<number, BuildingCounts>;

function emptyCounts(): BuildingCounts {
  return { total: 0, byRoom: { '0': 0, '1': 0, '2': 0, '3': 0, '4': 0 } };
}

type Step = 'building' | 'floor' | 'apartment' | 'apartment-detail';

export const MapSearchPanel: React.FC<IProps> = ({
  projectPlanId,
  announcementId,
  sectors,
  image,
  activePlan,
  onApartmentClick,
}) => {
  const dispatch = useDispatch();

  /* Announcement-scoped aggregates (per-building / per-floor room
     counts + entrance lists) from `/apartmentSaleSummary`. Replaces
     the full `/searchApartmentsV2` dump the panel used to pull just
     to compute these same numbers client-side. */
  const [saleSummary, setSaleSummary] = useState<SaleSummaryBuilding[]>([]);

  /* Distinct building numbers from `/apartmentAddressOptions` (~80 ms).
     The grid waits for both this and the `/apartmentSaleSummary`
     aggregates, so this is kept as the authoritative building list and
     as the fallback that keeps the map usable if the summary call
     fails. */
  const [saleBuildingNumbers, setSaleBuildingNumbers] = useState<number[] | null>(null);
  /* Loading flag for the Bina grid. Rendering it before the numbers
     arrive would show every building disabled, which reads as "the
     buttons don't work". We show a compact loader instead. */
  const [onSaleLoading, setOnSaleLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!projectPlanId) {
      setSaleBuildingNumbers(null);
      setOnSaleLoading(false);
      return;
    }
    let cancelled = false;
    setOnSaleLoading(true);
    setSaleBuildingNumbers(null);
    fetchApartmentAddressOptions({ projectPlanId, announcementId })
      .then((res) => {
        if (cancelled) return;
        setSaleBuildingNumbers(
          (res.buildingNumbers ?? []).map(Number).filter((n) => Number.isFinite(n)),
        );
        setOnSaleLoading(false);
      })
      .catch(() => {
        /* Leave the list null so `onSaleBuildings` falls back to the
           summary response once it lands, rather than locking the grid. */
        if (!cancelled) setOnSaleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectPlanId, announcementId]);

  /* Tracks the summary fetch separately from the grid's. The summary
     cells it feeds (Giriş especially) would otherwise sit on a "—"
     placeholder while it loads, which reads as "no entrances"
     rather than "still loading". */
  const [onSaleDetailsLoading, setOnSaleDetailsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!projectPlanId) {
      setSaleSummary([]);
      setOnSaleDetailsLoading(false);
      return;
    }
    let cancelled = false;
    setOnSaleDetailsLoading(true);
    setSaleSummary([]);
    fetchApartmentSaleSummary(projectPlanId, announcementId).then((buildings) => {
      if (cancelled) return;
      setSaleSummary(buildings);
      setOnSaleDetailsLoading(false);
    }).catch(() => {
      /* Non-fatal: the grid is already driven by the address-options
         call above; only the derived counts go missing. */
      if (!cancelled) setOnSaleDetailsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectPlanId, announcementId]);

  /* Buildings present in the announcement — set of buildingNumbers.
     Prefers the fast address-options list; falls back to deriving them
     from the summary if that call failed. */
  const onSaleBuildings = useMemo(
    () =>
      new Set(
        saleBuildingNumbers ??
          saleSummary
            .map((b) => Number(b.buildingNumber))
            .filter((n) => Number.isFinite(n)),
      ),
    [saleBuildingNumbers, saleSummary],
  );

  /* The map polygons stay inert until both fetches are in, for the same
     reason the Bina grid does: `onSaleBuildings` is populated by the fast
     address-options call, so without this the buildings would light up and
     accept hovers/clicks while the per-building counts behind the detail
     card were still loading. */
  const mapReady = !onSaleLoading && !onSaleDetailsLoading;

  /* Per-building summary: total apartments + per-room-type breakdown,
     used for the "Bina X satışda olan mənzillər" panel. */
  const countsByBuilding = useMemo<CountsByBuilding>(() => {
    const map: CountsByBuilding = {};
    for (const building of saleSummary) {
      const bn = Number(building.buildingNumber);
      if (!Number.isFinite(bn)) continue;
      const counts = emptyCounts();
      counts.total = building.total;
      for (const [room, cnt] of Object.entries(building.countsByRoom ?? {})) {
        counts.byRoom[room] = (counts.byRoom[room] ?? 0) + cnt;
      }
      map[bn] = counts;
    }
    return map;
  }, [saleSummary]);

  /* Aggregated entrance numbers per building — used by the summary
     cell on the building step (before a floor is highlighted).
     The server already sorts them numerically. */
  const entrancesByBuilding = useMemo(() => {
    const out: Record<number, string[]> = {};
    for (const building of saleSummary) {
      const bn = Number(building.buildingNumber);
      if (!Number.isFinite(bn)) continue;
      const list = (building.entrances ?? []).map(String).filter((e) => e !== '');
      if (list.length > 0) out[bn] = list;
    }
    return out;
  }, [saleSummary]);

  /* Instant entrance-number lookup for the Giriş summary cell.
     Without this, the cell falls back to the redux `floorData`
     fetch that only fires when the user reaches the apartment
     step, so the very first entry into a building shows "—".
     Derived from the summary, which is loaded up front, so the
     value is present immediately. */
  const entrancesByFloor = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const building of saleSummary) {
      const bn = Number(building.buildingNumber);
      if (!Number.isFinite(bn)) continue;
      for (const floor of building.floors ?? []) {
        const fn = Number(floor.floorNumber);
        if (!Number.isFinite(fn)) continue;
        const list = (floor.entrances ?? []).map(String).filter((e) => e !== '');
        if (list.length > 0) out[`${bn}:${fn}`] = list;
      }
    }
    return out;
  }, [saleSummary]);

  /* ---------- step 1 state ---------- */
  const buildings: Building[] = useMemo(() => {
    if (
      !image ||
      !sectors ||
      sectors.length === 0 ||
      !sectors[0].shape?.points ||
      sectors[0].shape.points.length === 0
    ) {
      return [];
    }
    return sectors.map((sec, idx) => {
      const c = centroidOf(sec.shape.points);
      let minX = Infinity;
      let maxX = -Infinity;
      for (const p of sec.shape.points) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
      }
      return {
        n: sec.buildingNumber ?? idx + 1,
        xPct: (c.x / image.width) * 100,
        yPct: (c.y / image.height) * 100,
        leftPct: (minX / image.width) * 100,
        rightPct: (maxX / image.width) * 100,
        sector: sec,
        pathD: buildPath(sec.shape),
      };
    });
  }, [image, sectors]);

  const gridBuildings = useMemo(
    () =>
      buildings
        .map(({ n, sector }) => ({
          n,
          sector,
          available: onSaleBuildings.has(n) ? 1 : 0,
        }))
        .sort((a, b) => a.n - b.n),
    [buildings, onSaleBuildings],
  );

  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null);
  /* Building the cursor is currently over on the project map. Only on-sale
     buildings set it — hovering one dims the rest of the map, matching the
     `invertHover` behaviour of the projects portal. */
  const [hoveredBuilding, setHoveredBuilding] = useState<number | null>(null);
  const [step, setStep] = useState<Step>('building');

  /* Hovered shape for in-image highlighting — both directions (grid hover
     highlights image shape, image hover highlights grid cell). */
  const [hoveredFloor, setHoveredFloor] = useState<ISectorFloor | undefined>();
  const [hoveredApartment, setHoveredApartment] = useState<IFloorApartment | undefined>();

  const [selectedFloor, setSelectedFloor] = useState<ISectorFloor | null>(null);
  const [selectedApartment, setSelectedApartment] = useState<IFloorApartment | null>(null);

  /* Reset on plan change. */
  useEffect(() => {
    setSelectedBuilding(null);
    setSelectedFloor(null);
    setSelectedApartment(null);
    setHoveredFloor(undefined);
    setHoveredApartment(undefined);
    setStep('building');
  }, [projectPlanId]);

  const selectedEntry = useMemo(
    () => buildings.find((b) => b.n === selectedBuilding) ?? null,
    [buildings, selectedBuilding],
  );

  /* ---------- step 2/3 data ---------- */
  /* Fire the sector fetch as soon as a building is selected — either
     on the building step (so the Bina/Giriş/Mərtəbə summary can show
     the real floor count immediately after the pick) or on the floor
     step. Same call SectorDetailPage uses (`GET /getSectorFloors/:id`). */
  useEffect(() => {
    const sec = selectedEntry?.sector;
    if ((step === 'building' || step === 'floor') && sec) {
      dispatch(sectorRedux.actions.getDetail(sec.id));
    }
  }, [dispatch, step, selectedEntry]);

  const sectorBranch = useSelector<IAppReduxState, IAsyncData<ISector>>(
    (s) => s.sector.details,
  );
  const sectorLoading = isLoading(sectorBranch);
  const sectorData = sectorBranch.data;


  /* Floor fetch — same call FloorDetailPage uses. We fire it as
     soon as the user highlights a floor on the floor step (not just
     on entering apartment step) so the Giriş summary cell shows
     real entrances on the very first visit instead of "—". */
  useEffect(() => {
    const sec = selectedEntry?.sector;
    if ((step === 'floor' || step === 'apartment') && sec && selectedFloor) {
      dispatch(floorRedux.actions.getDetail(sec.id, selectedFloor.id));
    }
  }, [dispatch, step, selectedEntry, selectedFloor]);

  const floorBranch = useSelector<IAppReduxState, IAsyncData<IFloor>>(
    (s) => s.floor.details,
  );
  const floorLoading = isLoading(floorBranch);
  const floorData = floorBranch.data;

  useEffect(() => {
    if (
      (step === 'apartment' || step === 'apartment-detail') &&
      selectedApartment
    ) {
      dispatch(apartmentRedux.actions.getDetail(selectedApartment.id));
    }
  }, [dispatch, step, selectedApartment]);

  const apartmentBranch = useSelector<IAppReduxState, IAsyncData<IApartment>>(
    (s) => s.apartment.details,
  );
  const apartmentLoading = isLoading(apartmentBranch);
  const apartmentData = apartmentBranch.data;

  /* Selecting a unit normally fires the detail fetch via the effect
     above. Re-clicking the SAME unit doesn't change state, so after a
     failed fetch the effect never re-fires and the price row would be
     gone for good — re-dispatch explicitly in that case so the click
     doubles as a retry. */
  function selectApartment(apt: IFloorApartment) {
    setSelectedApartment(apt);
    if (selectedApartment?.id === apt.id && isError(apartmentBranch)) {
      dispatch(apartmentRedux.actions.getDetail(apt.id));
    }
  }

  /* Room breakdown for an arbitrary building — the map hover card needs it
     for whichever building the cursor is over, not just the selected one.
     Every row is kept, including zeros, so the card always has the same
     five lines and doesn't jump around in height between buildings. */
  const breakdownFor = useCallback(
    (buildingNumber: number | null) => {
      if (buildingNumber == null) return [];
      const byRoom = countsByBuilding[buildingNumber]?.byRoom;
      if (!byRoom) return [];
      return [
        { label: '1 otaqlı studio mənzil', count: byRoom['0'] ?? 0 },
        { label: '1 otaqlı mənzil', count: byRoom['1'] ?? 0 },
        { label: '2 otaqlı mənzil', count: byRoom['2'] ?? 0 },
        { label: '3 otaqlı mənzil', count: byRoom['3'] ?? 0 },
        { label: '4 otaqlı mənzil', count: byRoom['4'] ?? 0 },
      ];
    },
    [countsByBuilding],
  );

  /* Which building the card is showing: hover wins while the cursor is over
     a building, otherwise the clicked one stays put. */
  const cardBuilding = hoveredBuilding ?? selectedBuilding;

  /* Clicking away from the map deselects the building outright — the card
     goes and so does the selection.
     Bound on mousedown so it lands before the map's own click handlers, and
     only on the building step: the later steps read `selectedBuilding` to
     render at all, so clearing it there would break the view.
     The left panel is exempt. Its controls (Növbəti, the Bina seçimi cells)
     act *on* the selection — `next()` reads `selectedEntry?.sector` — so
     wiping it on mousedown would make those clicks silently no-op. */
  useEffect(() => {
    if (step !== 'building' || selectedBuilding == null) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('.msp__building-card')) return;
      if (t.closest('aside')) return;
      /* Markers are hidden today, but keep them in the skip list so the
         selection survives a marker click if they ever come back. */
      if (t.closest('.msp__marker:not(:disabled)')) return;
      const shape = t.closest('.msp__map-shape');
      if (shape && !shape.classList.contains('msp__map-shape--disabled')) return;
      setSelectedBuilding(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [step, selectedBuilding]);

  /* Apartments on the current floor grouped by entrance, so the
     apartment step can list every entrance with its apartment
     numbers + room count before the user picks a specific unit. */
  const apartmentsByEntrance = useMemo(() => {
    if (!floorData?.apartments || floorData.apartments.length === 0) return [];
    const groups: Record<string, IFloorApartment[]> = {};
    for (const apt of floorData.apartments) {
      const key = String(apt.etrance ?? '—');
      if (!groups[key]) groups[key] = [];
      groups[key].push(apt);
    }
    const numeric = (s: string) => {
      const n = parseInt(s, 10);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };
    return Object.entries(groups)
      .sort(([a], [b]) => numeric(a) - numeric(b))
      .map(([entrance, apts]) => ({
        entrance,
        apartments: [...apts].sort(
          (a, b) => numeric(a.apartmentNumber) - numeric(b.apartmentNumber),
        ),
      }));
  }, [floorData]);

  /* Per-room-type breakdown for the currently-highlighted floor,
     same shape as `breakdownFor` but scoped to (building, floor). */
  const floorRoomBreakdown = useMemo(() => {
    if (selectedBuilding == null || !selectedFloor) return [];
    const targetFloor = Number(selectedFloor.number);
    if (!Number.isFinite(targetFloor)) return [];
    const building = saleSummary.find(
      (b) => Number(b.buildingNumber) === selectedBuilding,
    );
    const floor = building?.floors?.find(
      (f) => Number(f.floorNumber) === targetFloor,
    );
    const byRoom = floor?.countsByRoom ?? {};
    return [
      { label: 'Studio tipli mənzillər', count: byRoom['0'] ?? 0 },
      { label: '1 otaqlı mənzillər', count: byRoom['1'] ?? 0 },
      { label: '2 otaqlı mənzillər', count: byRoom['2'] ?? 0 },
      { label: '3 otaqlı mənzillər', count: byRoom['3'] ?? 0 },
      { label: '4 otaqlı mənzillər', count: byRoom['4'] ?? 0 },
    ].filter((r) => r.count > 0);
  }, [selectedBuilding, selectedFloor, saleSummary]);

  /* ---------- navigation ---------- */

  function next() {
    if (step === 'building') {
      const sec = selectedEntry?.sector;
      if (!sec) return;
      /* Pre-warm the apartment search so the parameter / address panels
         already have a result set if the user switches tabs. */
      dispatch(
        projectRedux.actions.searchApartments({
          projectPlanId,
          announcementId,
          page: 1,
          limit: 50,
          data: {
            sectorTypes: [{ sectorType: String(sec.buildingType) }],
            floor: { from: '1', to: String(sec.buildingType) },
            roomCounts: [],
            repairTypes: [],
          },
        }),
      );
      setStep('floor');
      return;
    }

    if (step === 'floor' && selectedFloor) {
      setStep('apartment');
      return;
    }

    if (step === 'apartment' && selectedApartment && selectedFloor && selectedEntry?.sector) {
      /* Done 10 → apartment-detail. Pre-warm the search-by-exact-unit
         so the other tabs land on the right result if the user
         switches over. Then bubble the picked apartment up to
         SaleAnnouncement, which renders the shared
         `ApartmentDetailInline` view above the map — so the user
         sees the same apartment-detail layout from every entry
         point (Map / Address / Parameter). */
      const sec = selectedEntry.sector;
      dispatch(
        projectRedux.actions.searchApartments({
          projectPlanId,
          announcementId,
          page: 1,
          limit: 50,
          data: {
            sectorTypes: [{ sectorType: String(sec.buildingType) }],
            floor: { from: String(selectedFloor.number), to: String(selectedFloor.number) },
            roomCounts: [{ roomCount: String(selectedApartment.roomCount) }],
            repairTypes: [],
          },
        }),
      );
      if (onApartmentClick) {
        onApartmentClick(selectedApartment);
        return;
      }
      /* Fallback for when no parent handler is wired — keep the
         original inline step-4 view. */
      setStep('apartment-detail');
      return;
    }
  }

  function back() {
    if (step === 'apartment-detail') { setStep('apartment'); return; }
    if (step === 'apartment') {
      /* First Geri after an apartment is picked (e.g. from the
         entrance panel) just clears the selection so the user
         returns to the entrance list on the same step. Only a
         second Geri walks back to the floor step. */
      if (selectedApartment) { setSelectedApartment(null); return; }
      setSelectedFloor(null); setStep('floor'); return;
    }
    if (step === 'floor') { setSelectedFloor(null); setHoveredFloor(undefined); setStep('building'); return; }
    setSelectedBuilding(null);
  }

  /* ---------- step 1 map dimensions ---------- */
  const hasPolygons = buildings.some((b) => b.pathD);
  const imgW = image?.width ?? 0;
  const imgH = image?.height ?? 0;

  /* ---------- form panel sections by step ---------- */
  /* Form title is the active project's name (per done14 / done15) —
     the project changes as the user picks a different card in
     "Layihə seçimi" above, so the panel header reflects which
     project they're currently drilling into. Falls back to a
     generic label when the announcement hasn't loaded yet. */
  const stepTitle = activePlan?.name_az ?? 'Xəritə üzrə mənzil seçimi';

  return (
    <div className="msp">
      {/* Portrait-orientation hint — pure CSS overlay that appears
          only on narrow, portrait viewports (phones and small
          tablets held upright). Rotating the device makes the
          media query stop matching and the overlay disappears,
          leaving the standard row layout below. */}
      <div className="msp__rotate-hint" role="dialog" aria-label="Cihazınızı üfüqi vəziyyətə çevirin">
        <svg
          className="msp__rotate-hint-icon"
          viewBox="0 0 48 48"
          aria-hidden
        >
          <rect x="10" y="4" width="16" height="28" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
          <rect x="14" y="8" width="8" height="20" rx="1" fill="currentColor" opacity="0.15" />
          <circle cx="18" cy="30" r="1.4" fill="currentColor" />
          <path
            d="M30 22a14 14 0 0 1 14 14M40 40l4-4-4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="msp__rotate-hint-text">
          Xəritə üzərində mənzil axtarışı yalnız üfüqi rejimdə göstərilir.
          Xahiş edirik cihazınızı yan çevirin.
        </p>
      </div>

      <aside className="msp__form">
        <div className="msp__form-body">
          <h3 className="msp__form-title">{stepTitle}</h3>

          {/* Step 1 grid — "Bina seçimi" */}
          {step === 'building' && (
            <div className="msp__section">
              <span className="msp__section-label">Bina seçimi</span>
              <div className="msp__grid">
                {/* Hold the grid until BOTH fetches are in. The fast
                    address-options call alone would let the buttons appear
                    while the per-building counts are still loading, so a
                    user could click through to a building whose data isn't
                    there yet. Everything loads, then everything is
                    clickable. */}
                {(gridBuildings.length === 0 || onSaleLoading || onSaleDetailsLoading) ? (
                  <div className="msp__loader" role="status" aria-live="polite">
                    <span className="msp__loader-dot" />
                    <span className="msp__loader-dot" />
                    <span className="msp__loader-dot" />
                    <span className="msp__sr-only">Yüklənir…</span>
                  </div>
                ) : (
                  gridBuildings.map(({ n, available }) => (
                    <button
                      key={n}
                      type="button"
                      className={`msp__cell ${selectedBuilding === n ? 'msp__cell--active' : ''}`}
                      disabled={available === 0}
                      onClick={() => setSelectedBuilding(n)}
                    >
                      {n}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Bina / Giriş / Mərtəbə sayı summary — the 3-column box at
              the top of the form for done14 + done15. Visible once a
              building is picked; values come from the selected sector /
              floor data (entrance count derived from the loaded floor's
              apartments when available, otherwise "—"). */}
          {(step === 'building' || step === 'floor' || step === 'apartment' || step === 'apartment-detail') &&
            selectedBuilding != null && (
              <div className="msp__summary">
                <div className="msp__summary-cell">
                  <span className="msp__summary-label">Bina</span>
                  <span className="msp__summary-value">{selectedBuilding}</span>
                </div>
                <span className="msp__summary-divider" aria-hidden />
                <div className="msp__summary-cell">
                  <span className="msp__summary-label">Giriş</span>
                  <span className="msp__summary-value">
                    {(() => {
                      const entrances = (() => {
                        if (selectedApartment?.etrance) return selectedApartment.etrance;
                        if (selectedBuilding != null && selectedFloor?.number != null) {
                          const key = `${selectedBuilding}:${Number(selectedFloor.number)}`;
                          const preloaded = entrancesByFloor[key];
                          if (preloaded && preloaded.length > 0) return preloaded.join(', ');
                        }
                        /* Building-level fallback covers the "just entered
                           the building, no floor picked yet" state on the
                           floor step and the whole building step. */
                        if (selectedBuilding != null) {
                          const perBuilding = entrancesByBuilding[selectedBuilding];
                          if (perBuilding && perBuilding.length > 0) return perBuilding.join(', ');
                        }
                        if (floorData?.apartments && floorData.apartments.length > 0) {
                          return Array.from(
                            new Set(floorData.apartments.map((a) => a.etrance)),
                          ).join(', ');
                        }
                        return null;
                      })();

                      if (entrances) return entrances;

                      /* Nothing resolved yet. While either source is still
                         in flight show a loader — a bare "—" here reads as
                         "this building has no entrances" rather than
                         "still loading", which is wrong for the seconds the
                         apartment list takes to arrive. */
                      if (onSaleDetailsLoading || floorLoading) {
                        return (
                          <span
                            className="msp__loader msp__loader--inline"
                            role="status"
                            aria-live="polite"
                          >
                            <span className="msp__loader-dot" />
                            <span className="msp__loader-dot" />
                            <span className="msp__loader-dot" />
                            <span className="msp__sr-only">Yüklənir…</span>
                          </span>
                        );
                      }
                      return '—';
                    })()}
                  </span>
                </div>
                <span className="msp__summary-divider" aria-hidden />
                <div className="msp__summary-cell">
                  {/* On the building and floor steps the field shows
                      "Mərtəbə sayı" + the sector's total floor count
                      (a stable value that doesn't flip when the user
                      highlights a floor). Only on the apartment /
                      apartment-detail steps does it become "Mərtəbə"
                      + the currently-picked floor. */}
                  <span className="msp__summary-label">
                    {step === 'building' || step === 'floor' ? 'Mərtəbə sayı' : 'Mərtəbə'}
                  </span>
                  <span className="msp__summary-value">
                    {step === 'building' || step === 'floor'
                      ? selectedEntry?.sector?.floorCount ??
                        sectorData?.countOfFloors ??
                        '—'
                      : selectedFloor?.number ?? '—'}
                  </span>
                </div>
                {(step === 'apartment' || step === 'apartment-detail') &&
                  selectedApartment?.apartmentNumber != null && (
                    <>
                      <span className="msp__summary-divider" aria-hidden />
                      <div className="msp__summary-cell">
                        <span className="msp__summary-label">Mənzil</span>
                        <span className="msp__summary-value">
                          {selectedApartment.apartmentNumber}
                        </span>
                      </div>
                    </>
                  )}
              </div>
            )}

          {step === 'floor' && (
            <div className="msp__section">
              <span className="msp__section-label">Mərtəbə seçimi</span>
              <div className="msp__grid">
                {sectorLoading && (
                  <div className="msp__loader" role="status" aria-live="polite">
                    <span className="msp__loader-dot" />
                    <span className="msp__loader-dot" />
                    <span className="msp__loader-dot" />
                    <span className="msp__sr-only">Yüklənir…</span>
                  </div>
                )}
                {!sectorLoading && sectorData?.floors?.map((f) => {
                  /* Every floor is selectable now — including ones with no
                     on-sale apartments — so the user can browse the whole
                     building. Non-sale apartments carry their Satılıb /
                     Dövlət Sifarişi status on the apartment step. */
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className={`msp__cell ${selectedFloor?.id === f.id ? 'msp__cell--active' : ''}`}
                      onClick={() => {
                        setSelectedFloor(f);
                        setHoveredFloor(f);
                      }}
                      onMouseEnter={() => setHoveredFloor(f)}
                      onMouseLeave={() => setHoveredFloor(selectedFloor ?? undefined)}
                    >
                      {f.number}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'apartment' && (
            <div className="msp__section">
              <span className="msp__section-label">Mərtəbə seçimi</span>
              <div className="msp__grid">
                {sectorData?.floors?.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`msp__cell ${selectedFloor?.id === f.id ? 'msp__cell--active' : ''}`}
                    onClick={() => {
                      if (selectedFloor?.id !== f.id) {
                        setSelectedFloor(f);
                        setSelectedApartment(null);
                        setHoveredFloor(f);
                      }
                    }}
                  >
                    {f.number}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Floor-scoped room breakdown — only when a floor is picked
              in the floor step, before the whole-building breakdown. */}
          {step === 'floor' && selectedFloor && floorRoomBreakdown.length > 0 && (
            <div className="msp__section">
              <span className="msp__section-label">
                Mərtəbə {selectedFloor.number} satışda olan mənzillər
              </span>
              <ul className="msp__rooms">
                {floorRoomBreakdown.map((r) => (
                  <li key={r.label} className="msp__rooms-row">
                    <span className="msp__rooms-label">{r.label}</span>
                    <span className="msp__rooms-count">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The per-building room breakdown used to live here. It now
              renders as a card anchored to the building on the map (see
              `msp__building-card`), shown on hover and pinned on click. */}

          {/* Apartment step: before the user picks a specific unit,
              list every entrance on the current floor with its
              apartment numbers + room count. Clicking a row selects
              that apartment. */}
          {step === 'apartment' && !selectedApartment && apartmentsByEntrance.length > 0 && (() => {
            /* Any apartment whose status is AVAILABLE is enterable
               from the panel, regardless of whether it's part of
               the current announcement's on-sale set. Only SOLD /
               GOVERNMENT / INVESTOR rows stay locked. */
            const isOnSale = (apt: IFloorApartment) =>
              apt.status === EApartmentStatus.AVAILABLE;
            const statusLabel = (apt: IFloorApartment) => {
              switch (apt.status) {
                case EApartmentStatus.SOLD:
                  return 'Satılıb';
                case EApartmentStatus.GOVERNMENT:
                  return 'Dövlət sifarişi';
                case EApartmentStatus.INVESTOR:
                  return 'İnvestor';
                default:
                  return '—';
              }
            };
            return (
              <div className="msp__section msp__section--entrances">
                {apartmentsByEntrance.map((group) => (
                  <div key={group.entrance} className="msp__entrance">
                    <span className="msp__section-label">Giriş {group.entrance}</span>
                    <ul className="msp__rooms">
                      {group.apartments.map((apt) => {
                        const onSale = isOnSale(apt);
                        return (
                          <li
                            key={apt.id}
                            className={
                              'msp__rooms-row' +
                              (onSale ? ' msp__rooms-row--clickable' : ' msp__rooms-row--disabled')
                            }
                            onClick={onSale ? () => selectApartment(apt) : undefined}
                          >
                            <span className="msp__rooms-label">Mənzil {apt.apartmentNumber}</span>
                            <span className="msp__rooms-count">
                              {onSale
                                ? (apt.roomCount === 0 ? 'Studio' : `${apt.roomCount} otaqlı`)
                                : statusLabel(apt)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            );
          })()}

          {(step === 'apartment' || step === 'apartment-detail') && selectedApartment && (
            <div className="msp__spec">
              <div className="msp__spec-list">
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Otaq sayı</span>
                  <span className="msp__spec-value">
                    {selectedApartment.roomCount === 0
                      ? 'Studio'
                      : `${selectedApartment.roomCount}`}
                  </span>
                </div>
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Xarici perimetr üzrə sahə</span>
                  <span className="msp__spec-value">
                    {selectedApartment.externalSize} m²
                  </span>
                </div>
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Daxili perimetr üzrə sahə
                    <br/> <small>(Dövlət qeydiyyat sənədi üzrə)</small>
                  </span>
                  <span className="msp__spec-value">
                    {selectedApartment.internalSize} m²
                  </span>
                </div>
              </div>

              {(() => {
                /* Price comes from the per-apartment detail fetch that
                   fires as soon as a unit is selected (the redux
                   `apartment.details` branch) — but only once it
                   actually belongs to this apartment. That fetch lags
                   a step behind while a new one is in flight, so using
                   it unguarded would show the previously selected
                   flat's price. The building and entrance are part of
                   the match: different buildings (and different
                   entrances within one — Şirvan bina 21) hold units
                   sharing an apartment number on the same floor, and
                   those are different flats with different prices. */
                const detailMatches =
                  apartmentData != null &&
                  Number(apartmentData.buildingNumber) === Number(selectedBuilding) &&
                  `${apartmentData.apartmentNumber}` === `${selectedApartment.apartmentNumber}` &&
                  `${apartmentData.entranceNumber}` === `${selectedApartment.etrance}` &&
                  Number(apartmentData.floorNumber) === Number(selectedFloor?.number);

                if (!detailMatches) {
                  /* Detail still in flight for this unit — show the
                     row with an inline loader so the price appears in
                     place instead of popping in from nowhere. Once
                     the fetch settles without a match (failed), drop
                     the row entirely. */
                  if (!apartmentLoading) return null;
                  return (
                    <div className="msp__spec-price">
                      <span className="msp__spec-price-label">Mənzilin satış qiyməti</span>
                      <span className="msp__spec-price-value">
                        <span
                          className="msp__loader msp__loader--inline"
                          role="status"
                          aria-live="polite"
                        >
                          <span className="msp__loader-dot" />
                          <span className="msp__loader-dot" />
                          <span className="msp__loader-dot" />
                          <span className="msp__sr-only">Yüklənir…</span>
                        </span>
                      </span>
                    </div>
                  );
                }

                const price = apartmentData?.price;
                if (price == null) return null;
                return (
                  <div className="msp__spec-price">
                    <span className="msp__spec-price-label">Mənzilin satış qiyməti</span>
                    <span className="msp__spec-price-value">
                      {price} AZN
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="msp__actions">
          <button type="button" className="msp-btn msp-btn--ghost" onClick={back}>
            Geri
          </button>
          {/* Apartment-detail is terminal — per done14 the right-side
              info panel already shows everything the user needs, so we
              drop Növbəti entirely on this step rather than render it
              disabled. */}
          {step !== 'apartment-detail' && (
            <button
              type="button"
              className="msp-btn msp-btn--primary"
              onClick={next}
              disabled={
                (step === 'building' && (selectedBuilding == null || !selectedEntry?.sector)) ||
                (step === 'floor' && !selectedFloor) ||
                (step === 'apartment' && (
                  !selectedApartment ||
                  selectedApartment.status !== EApartmentStatus.AVAILABLE
                ))
              }
            >
              Növbəti
            </button>
          )}
        </div>
      </aside>

      {/* ---------- right pane changes per step ---------- */}
      <div className="msp__map">
        {step === 'building' && image?.url && (
          /* Project map + clickable building markers — only rendered
             once we have a real map image from the API. */
          <>
            <div
              className="msp__map-frame"
              style={{ aspectRatio: `${imgW} / ${imgH}` } as React.CSSProperties}
            >
              <img
                src={image.url}
                alt=""
                className="msp__map-img"
              />

              {hasPolygons && (
                <svg
                  className="msp__map-shapes"
                  viewBox={`0 0 ${imgW} ${imgH}`}
                  preserveAspectRatio="xMidYMid meet"
                  aria-hidden
                >
                  {/* Dim everything except the focused building. One path
                      covering the whole map, with that building's outline
                      appended and fill-rule="evenodd" punching it out — the
                      same "invert the hovered shape" idea the projects
                      portal gets from <ShapeViewer invertHover>. Drawn
                      before the building paths so the selected building's
                      orange fill stays crisp on top.

                      Keyed off `cardBuilding`, not the raw hover, so it
                      tracks the card exactly: moving the cursor off a
                      clicked building leaves both in place, and only a
                      click to the side clears them. */}
                  {(() => {
                    const hovered = buildings.find(
                      (b) => b.n === cardBuilding && b.pathD,
                    );
                    if (!hovered) return null;
                    return (
                      <path
                        className="msp__map-dim"
                        d={`M0 0 H${imgW} V${imgH} H0 Z ${hovered.pathD}`}
                        fillRule="evenodd"
                        fill="rgba(34, 58, 94, 0.6)"
                        pointerEvents="none"
                      />
                    );
                  })()}

                  {buildings.map(({ n, pathD }) => {
                    if (!pathD) return null;
                    const clickable = mapReady && onSaleBuildings.has(n);
                    return (
                      <path
                        key={n}
                        className={
                          'msp__map-shape' +
                          (clickable ? '' : ' msp__map-shape--disabled') +
                          (selectedBuilding === n ? ' msp__map-shape--selected' : '')
                        }
                        d={pathD}
                        /* Near-invisible fill rather than `none` so the shape
                           still receives pointer events. The selected look is
                           entirely the white outline + glow from
                           `--selected` in CSS — no colour wash. */
                        fill="rgba(255,255,255,0.001)"
                        stroke="transparent"
                        /* Buildings with nothing on sale are refused, not
                           silently inert — same not-allowed cursor the
                           disabled markers and buttons already use. While
                           the data is still loading nothing is refused yet,
                           so show `progress` rather than a deny cursor. */
                        style={{
                          cursor: !mapReady ? 'progress' : clickable ? 'pointer' : 'not-allowed',
                        }}
                        onClick={clickable ? () => setSelectedBuilding(n) : undefined}
                        onMouseEnter={clickable ? () => setHoveredBuilding(n) : undefined}
                        onMouseLeave={clickable ? () => setHoveredBuilding(null) : undefined}
                      />
                    );
                  })}
                </svg>
              )}

              {buildings.map(({ n, xPct, yPct }) => {
                const clickable = onSaleBuildings.has(n);
                return (
                  <button
                    key={n}
                    type="button"
                    className={`msp__marker ${selectedBuilding === n ? 'msp__marker--active' : ''}`}
                    style={{ left: `${xPct}%`, top: `${yPct}%` }}
                    disabled={!clickable}
                    onClick={() => setSelectedBuilding(n)}
                    onMouseEnter={() => clickable && setHoveredBuilding(n)}
                    onMouseLeave={() => clickable && setHoveredBuilding(null)}
                    aria-label={`Bina ${n}`}
                  >
                    <span className="msp__marker-num">{n}</span>
                  </button>
                );
              })}

              {/* Detail card for the hovered (or pinned) building. Anchored
                  to the edge of the building's footprint — not its centroid,
                  which would put the card on top of the building it
                  describes — and flipped to the other side near the map's
                  right edge so it never runs off. */}
              {cardBuilding != null && (() => {
                const b = buildings.find((x) => x.n === cardBuilding);
                if (!b) return null;
                const rows = breakdownFor(cardBuilding);
                if (rows.length === 0) return null;
                const entrances = entrancesByBuilding[cardBuilding];
                /* Fall back to the centroid for shapes with no computed
                   extent, so the card still renders somewhere sane. */
                const leftEdge = b.leftPct ?? b.xPct;
                const rightEdge = b.rightPct ?? b.xPct;
                const flip = rightEdge > 55;
                return (
                  <div
                    className={
                      'msp__building-card' +
                      (selectedBuilding === cardBuilding ? ' msp__building-card--pinned' : '') +
                      (flip ? ' msp__building-card--left' : '') +
                      /* Vertically the card is centred on the building, but
                         near the top/bottom edge that would hang it off the
                         map — so anchor its top or bottom edge instead. */
                      (b.yPct > 72
                        ? ' msp__building-card--above'
                        : b.yPct < 28
                          ? ' msp__building-card--below'
                          : '')
                    }
                    style={{
                      /* Anchor to the far side of the footprint so the gap in
                         the CSS transform is measured from the building's
                         edge, never from its middle. */
                      left: `${flip ? leftEdge : rightEdge}%`,
                      top: `${b.yPct}%`,
                    }}
                  >
                    <div className="msp__building-card__head">
                      <span className="msp__building-card__title">Bina {cardBuilding}</span>
                      {entrances && entrances.length > 0 && (
                        <span className="msp__building-card__entrances">
                          Giriş {entrances.join(', ')}
                        </span>
                      )}
                    </div>
                    <ul className="msp__building-card__rows">
                      {rows.map((r) => (
                        <li key={r.label} className="msp__building-card__row">
                          <span className="msp__building-card__label">{r.label}</span>
                          <span className="msp__building-card__count">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {step === 'floor' && (
          /* done15: building photo with a numbered badge overlaid on
             each floor (right edge of the building, at that floor's
             level). The badges are the primary click target — no red
             strip overlay on the photo itself. ShapeViewer is still
             mounted so the floor bands stay clickable as a secondary
             path, but its overlay paint is hidden in CSS (transparent
             fill + transparent stroke for `--floor`). */
          <div className="msp__viewer msp__viewer--floor">
            {sectorLoading && <div className="msp__loading">Yüklənir…</div>}
            {!sectorLoading && sectorData?.image && (
              <div className="msp__floor-stage">
                {(() => {
                  /* All floors are now selectable — show every floor band
                     on the photo and keep every numbered badge enabled.
                     (Previously floors without on-sale apartments were
                     filtered out / greyed.) */
                  const visibleFloors = sectorData.floors ?? [];
                  return (
                    <>
                      <ShapeViewer
                        image={sectorData.image}
                        shapes={visibleFloors.map((f) => f.shape)}
                        hoveredShape={hoveredFloor?.shape}
                        hoverColor="transparent"
                        renderShapeLink={() => ''}
                        renderShapeOnClickLink={(shape) => {
                          const f = visibleFloors.find((fl) => fl.shape.id === shape.id);
                          if (f) {
                            setSelectedFloor(f);
                            setHoveredFloor(f);
                          }
                        }}
                        renderHovered={() => null}
                        onHoveredChanged={(shape) => {
                          const f = visibleFloors.find((fl) => fl.shape.id === shape?.id);
                          setHoveredFloor(f ?? selectedFloor ?? undefined);
                        }}
                      />
                      {sectorData.floors && (
                        <FloorBadges
                          floors={sectorData.floors}
                          image={sectorData.image}
                          selectedId={selectedFloor?.id ?? null}
                          hoveredId={hoveredFloor?.id ?? null}
                          enabledFloorNumbers={undefined}
                          onSelect={(f) => {
                            setSelectedFloor(f);
                            setHoveredFloor(f);
                          }}
                          onHover={(f) => setHoveredFloor(f ?? selectedFloor ?? undefined)}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {!sectorLoading && !sectorData?.image && (
              <div className="msp__loading">Bina {selectedBuilding} üçün plan tapılmadı.</div>
            )}
          </div>
        )}

        {step === 'apartment' && (
          /* Done 10: floor plan with each apartment as a clickable
             shape. Stage matches the done14 apartment-detail aesthetic
             — a blurred copy of the project image as the backdrop
             with a dark tint, then the floor plan centred on top, and
             a persistent orange overlay drawn over the selected
             apartment so it stays highlighted even when the cursor
             leaves the shape (ShapeViewer's own overlay disappears on
             mouse-leave because nothing forces a hover-shape re-emit
             when the prop value is unchanged). */
          <div className="msp__viewer msp__viewer--apartment">
            {floorLoading && <div className="msp__loading">Yüklənir…</div>}
            {!floorLoading && floorData?.image && (
              <>
                <div
                  className="msp__plan-backdrop"
                  style={{ backgroundImage: `url("${backdropImg}")` }}
                  aria-hidden
                />
                <div className="msp__plan-wrap">
                  {(() => {
                    /* Every apartment on the floor is rendered. Units in
                       this announcement's on-sale set stay clickable; the
                       rest are painted with their Satılıb / Dövlət
                       Sifarişi status label (non-clickable), matching the
                       Layihələr floor view. */
                    const allApartments = floorData.apartments ?? [];
                    /* Same rule as the entrance panel: any AVAILABLE
                       apartment is enterable directly from the plan;
                       SOLD / GOVERNMENT / INVESTOR shapes stay
                       non-clickable and carry their status label. */
                    const isOnSale = (apt: IFloorApartment) =>
                      apt.status === EApartmentStatus.AVAILABLE;
                    return (
                      <ShapeViewer
                        image={floorData.image}
                        shapes={allApartments.map((a) => a.shape)}
                        hoveredShape={hoveredApartment?.shape}
                        hoverColor="transparent"
                        renderShapeLink={() => ''}
                        renderShapeOverlayText={(shape) => {
                          const apt = allApartments.find((a) => a.shape.id === shape.id);
                          /* On-sale units stay interactive; the rest
                             become non-clickable (returning any array
                             — even empty — triggers ShapeViewer's
                             non-clickable path branch). The visible
                             status text is drawn by our own overlay
                             below at a readable size, so we return
                             [] here to skip ShapeViewer's built-in
                             tiny fontSize:70 label. */
                          if (!apt || isOnSale(apt)) return null;
                          return [];
                        }}
                        renderShapeOnClickLink={(shape) => {
                          const apt = allApartments.find((a) => a.shape.id === shape.id);
                          if (apt && isOnSale(apt)) {
                            selectApartment(apt);
                            setHoveredApartment(apt);
                          }
                        }}
                        renderHovered={() => null}
                        onHoveredChanged={(shape) => {
                          const apt = allApartments.find((a) => a.shape.id === shape?.id);
                          setHoveredApartment(
                            apt && isOnSale(apt) ? apt : selectedApartment ?? undefined,
                          );
                        }}
                      />
                    );
                  })()}
                  {/* Status labels for non-available apartments —
                      drawn in a pointer-events:none overlay so the
                      shape's non-clickable behavior from ShapeViewer
                      is preserved. Sized in viewBox units so the
                      label stays legible at any display size. */}
                  {(() => {
                    const nonSale = (floorData.apartments ?? []).filter(
                      (a) => a.status !== EApartmentStatus.AVAILABLE,
                    );
                    if (nonSale.length === 0) return null;
                    const label = (apt: IFloorApartment) => {
                      switch (apt.status) {
                        case EApartmentStatus.SOLD:
                          return ['Satılıb'];
                        case EApartmentStatus.GOVERNMENT:
                          return ['Dövlət', 'sifarişi'];
                        case EApartmentStatus.INVESTOR:
                          return ['İnvestor'];
                        default:
                          return [] as string[];
                      }
                    };
                    return (
                      <svg
                        className="msp__status-labels"
                        viewBox={`0 0 ${floorData.image.width} ${floorData.image.height}`}
                        preserveAspectRatio="none"
                        aria-hidden
                      >
                        {nonSale.map((apt) => {
                          const pts = apt.shape?.points ?? [];
                          if (pts.length === 0) return null;
                          const xs = pts.map((p) => p.x);
                          const ys = pts.map((p) => p.y);
                          const minX = Math.min(...xs);
                          const maxX = Math.max(...xs);
                          const minY = Math.min(...ys);
                          const maxY = Math.max(...ys);
                          const cx = (minX + maxX) / 2;
                          const cy = (minY + maxY) / 2;
                          const lines = label(apt);
                          if (lines.length === 0) return null;
                          /* Size the label to the apartment's own
                             bounding box so it stays readable
                             regardless of the plan's viewBox scale.
                             We aim for a text block ~55% of the
                             shape width, split across the label's
                             line count. */
                          const shapeW = maxX - minX;
                          const shapeH = maxY - minY;
                          const longest = Math.max(...lines.map((l) => l.length));
                          const byWidth = (shapeW * 0.55) / (longest * 0.55);
                          const byHeight = (shapeH * 0.65) / lines.length;
                          const fontSize = Math.max(24, Math.min(byWidth, byHeight));
                          const dy = fontSize * 1.1;
                          const startY = cy - ((lines.length - 1) * dy) / 2;
                          return (
                            <text
                              key={apt.id}
                              x={cx}
                              y={startY}
                              textAnchor="middle"
                              alignmentBaseline="middle"
                              fontSize={fontSize}
                              className="msp__status-label"
                            >
                              {lines.map((t, i) => (
                                <tspan key={t} x={cx} dy={i === 0 ? 0 : dy}>
                                  {t}
                                </tspan>
                              ))}
                            </text>
                          );
                        })}
                      </svg>
                    );
                  })()}

                  {selectedApartment?.shape && (
                    <svg
                      className="msp__selected-overlay"
                      viewBox={`0 0 ${floorData.image.width} ${floorData.image.height}`}
                      preserveAspectRatio="none"
                      aria-hidden
                    >
                      <path
                        d={buildPath(selectedApartment.shape)}
                        className="msp__selected-shape"
                      />
                    </svg>
                  )}
                </div>

                {/* Compass — same rotation logic as the Layihələr
                    floor detail page. Reads the floor's compass_id
                    off the floor detail response. */}
                {floorData.compass != null && (
                  <div className="msp__compass">
                    <Compass compass={floorData.compass} />
                  </div>
                )}
              </>
            )}
            {!floorLoading && !floorData?.image && (
              <div className="msp__loading">
                {selectedFloor?.number ? `Mərtəbə ${selectedFloor.number} üçün plan tapılmadı.` : '—'}
              </div>
            )}
          </div>
        )}

        {step === 'apartment-detail' && (
          /* done14: split right pane — apartment plan on the left,
             dark "Mənzil haqqında məlumat" sidebar on the right with
             the selected apartment's key specs. */
          <div className="msp__viewer msp__viewer--detail">
            {apartmentLoading && (
              <div className="msp__loading">Yüklənir…</div>
            )}
            {!apartmentLoading && (apartmentData?.image?.url || apartmentData) && (
              <>
                <div className="msp__detail-plan">
                  {apartmentData?.image?.url ? (
                    <div
                      className="msp__detail-frame"
                      style={{ aspectRatio: `${apartmentData.image.width} / ${apartmentData.image.height}` } as React.CSSProperties}
                    >
                      <img
                        src={apartmentData.image.url}
                        alt=""
                        className="msp__detail-img"
                      />
                    </div>
                  ) : (
                    <div className="msp__loading">
                      {selectedApartment?.apartmentNumber
                        ? `Mənzil ${selectedApartment.apartmentNumber} üçün plan tapılmadı.`
                        : '—'}
                    </div>
                  )}
                </div>

                {apartmentData && (
                  <aside className="msp__detail-info" aria-label="Mənzil haqqında məlumat">
                    <h4 className="msp__detail-info-title">Mənzil haqqında məlumat</h4>
                    <ul className="msp__detail-info-list">
                      {apartmentData.buildingNumber != null && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Bina</span>
                          <span className="msp__detail-info-value">{apartmentData.buildingNumber}</span>
                        </li>
                      )}
                      {apartmentData.entranceNumber && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Giriş</span>
                          <span className="msp__detail-info-value">{apartmentData.entranceNumber}</span>
                        </li>
                      )}
                      {apartmentData.floorNumber && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Mərtəbə</span>
                          <span className="msp__detail-info-value">{apartmentData.floorNumber}</span>
                        </li>
                      )}
                      {apartmentData.apartmentNumber && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Mənzil №</span>
                          <span className="msp__detail-info-value">{apartmentData.apartmentNumber}</span>
                        </li>
                      )}
                      <li className="msp__detail-info-row">
                        <span className="msp__detail-info-label">Otaq sayı</span>
                        <span className="msp__detail-info-value">
                          {apartmentData.roomCount === 0 ? 'Studio' : `${apartmentData.roomCount} otaqlı`}
                        </span>
                      </li>
                      {apartmentData.internalSize != null && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Daxili sahə</span>
                          <span className="msp__detail-info-value">{apartmentData.internalSize} m²</span>
                        </li>
                      )}
                      {apartmentData.externalSize != null && (
                        <li className="msp__detail-info-row">
                          <span className="msp__detail-info-label">Ümumi sahə</span>
                          <span className="msp__detail-info-value">{apartmentData.externalSize} m²</span>
                        </li>
                      )}
                      {apartmentData.price != null && (
                        <li className="msp__detail-info-row msp__detail-info-row--price">
                          <span className="msp__detail-info-label">Mənzilin satış qiyməti</span>
                          <span className="msp__detail-info-value">
                            {apartmentData.price} AZN
                          </span>
                        </li>
                      )}
                    </ul>
                  </aside>
                )}
              </>
            )}
            {!apartmentLoading && !apartmentData && (
              <div className="msp__loading">
                {selectedApartment?.apartmentNumber
                  ? `Mənzil ${selectedApartment.apartmentNumber} üçün plan tapılmadı.`
                  : '—'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapSearchPanel;
